import { normalizeChapterContentForStorage } from "./chapterContent";
import type { Book, Chapter, ChapterComment } from "./models";
import { createDefaultBook } from "./sbkCompat";
import { withSpineOrderAndNumbers } from "./bookSpine";
import { mapLegacyVariations } from "./mapLegacyVariations";
import { averageAssessmentScore, normalizeScoreBlend } from "./scoreBlend";

const LEGACY_COMMENT_TYPES = new Set(["suggestion", "issue", "praise", "question", "note", "highlight"]);

function emptyTabData(): Chapter["tabData"] {
  return {
    storyCraft: "",
    characters: "",
    locations: "",
    timeline: "",
    themesMotifs: "",
    summary: "",
  };
}

/**
 * Tab text stored with the chapter wins; local values fill any key the library
 * has not seen yet, so an older client cannot blank a tab it never sent.
 */
function tabDataFromLegacyChapter(
  raw: unknown,
  existing: Chapter["tabData"] | undefined
): Chapter["tabData"] {
  const base = existing ?? emptyTabData();
  if (!raw || typeof raw !== "object") return base;
  const source = raw as Record<string, unknown>;
  const merged = { ...base };
  for (const key of Object.keys(base) as Array<keyof Chapter["tabData"]>) {
    const value = source[key];
    if (typeof value === "string" && value.trim()) merged[key] = value;
  }
  return merged;
}

function editorAverageFromLegacyChapter(raw: Record<string, unknown>): {
  average: number | null;
  assessment: unknown | null;
  summary: string | null;
} {
  const fb = raw.editorChapterFeedback;
  let assessment: unknown | null = null;
  let summary: string | null = null;
  if (fb && typeof fb === "object") {
    const rec = fb as Record<string, unknown>;
    assessment = rec.assessment ?? null;
    summary = typeof rec.summary === "string" ? rec.summary : null;
  } else if (raw.editorAssessment) {
    assessment = raw.editorAssessment;
  }
  if (typeof raw.editorSummary === "string" && !summary) summary = raw.editorSummary;

  const average =
    typeof raw.editorAverage === "number" && Number.isFinite(raw.editorAverage)
      ? raw.editorAverage
      : averageAssessmentScore(assessment);

  return { average, assessment, summary };
}

function mapLegacyComment(raw: Record<string, unknown>): ChapterComment {
  const rawType = typeof raw.type === "string" ? raw.type.trim() : "";
  const type = LEGACY_COMMENT_TYPES.has(rawType) ? rawType : "note";
  const createdByRaw = raw.createdBy;
  const createdBy: ChapterComment["createdBy"] =
    createdByRaw === "ai" || createdByRaw === "AI" ? "ai" : "user";
  const anchorObj =
    raw.anchor && typeof raw.anchor === "object"
      ? (raw.anchor as { from?: unknown; to?: unknown; excerpt?: unknown })
      : null;
  const rf = Number(raw.rangeFrom);
  const rt = Number(raw.rangeTo);
  const anchorFromRange: ChapterComment["anchor"] =
    Number.isFinite(rf) && Number.isFinite(rt) && rf >= 0 && rt > rf
      ? { from: rf, to: rt }
      : undefined;
  const anchor: ChapterComment["anchor"] =
    anchorObj && typeof anchorObj.from === "number" && typeof anchorObj.to === "number"
      ? {
          from: anchorObj.from,
          to: anchorObj.to,
          excerpt: typeof anchorObj.excerpt === "string" ? anchorObj.excerpt : undefined,
        }
      : anchorFromRange;
  const authorUserId =
    typeof raw.authorUserId === "string" && raw.authorUserId.trim() ? raw.authorUserId.trim() : undefined;
  const parentRaw = raw.parentCommentId;
  const parentCommentId =
    typeof parentRaw === "string" && parentRaw.trim() ? parentRaw.trim() : null;
  const blockRaw = raw.blockId;
  const blockId = typeof blockRaw === "string" && blockRaw.trim() ? blockRaw.trim() : null;
  return {
    id: typeof raw.id === "string" && raw.id.trim() ? raw.id : `comment-${Date.now()}`,
    text: typeof raw.text === "string" ? raw.text : "",
    type,
    resolved: Boolean(raw.resolved),
    createdAt: typeof raw.createdAt === "string" && raw.createdAt.trim() ? raw.createdAt : new Date().toISOString(),
    createdBy,
    targetText: typeof raw.targetText === "string" ? raw.targetText : undefined,
    isStale: Boolean(raw.isStale),
    authorUserId,
    authorDisplayName: typeof raw.authorDisplayName === "string" ? raw.authorDisplayName : undefined,
    parentCommentId,
    blockId,
    anchor,
  };
}

function legacyContentToStored(content: unknown): string {
  return normalizeChapterContentForStorage(content);
}

function legacyChapterToDesktop(raw: Record<string, unknown>, existing: Chapter | undefined, fallbackOrder: number): Chapter {
  const order = typeof raw.order === "number" && Number.isFinite(raw.order) ? raw.order : fallbackOrder;
  const commentsIn = Array.isArray(raw.comments) ? raw.comments : [];
  const comments = commentsIn
    .filter((c): c is Record<string, unknown> => typeof c === "object" && c !== null)
    .map((c) => mapLegacyComment(c));
  const kindRaw = raw.kind;
  const kind =
    typeof kindRaw === "string" &&
    [
      "chapter",
      "half_title",
      "title_page",
      "copyright",
      "dedication",
      "epigraph",
      "foreword",
      "preface",
      "prologue",
      "toc",
      "section_break",
      "map",
      "epilogue",
      "afterword",
      "acknowledgments",
      "about_author",
      "also_by",
      "appendix",
      "excerpt",
      "custom",
    ].includes(kindRaw)
      ? (kindRaw as Chapter["kind"])
      : existing?.kind ?? "chapter";
  const chapterNumberRaw = raw.chapterNumber ?? raw.chapter_number;
  const chapterNumber =
    kind === "chapter"
      ? typeof chapterNumberRaw === "number" && Number.isFinite(chapterNumberRaw)
        ? chapterNumberRaw
        : existing?.chapterNumber ?? null
      : null;

  const editorScore = editorAverageFromLegacyChapter(raw);
  const baseMeta =
    existing?.metadata ??
    ({
      status: "draft" as const,
      tags: [] as string[],
      keywords: [] as string[],
      label: "",
      customFields: {} as Record<string, unknown>,
    });
  const customFields: Record<string, unknown> = {
    ...(baseMeta.customFields && typeof baseMeta.customFields === "object"
      ? baseMeta.customFields
      : {}),
  };
  if (editorScore.average != null) customFields.editorAverage = editorScore.average;
  if (editorScore.assessment != null) customFields.editorAssessment = editorScore.assessment;
  if (editorScore.summary != null) customFields.editorSummary = editorScore.summary;

  return {
    id: typeof raw.id === "string" && raw.id.trim() ? raw.id : existing?.id ?? `chapter-${fallbackOrder}`,
    title: typeof raw.title === "string" && raw.title.trim() ? raw.title : existing?.title ?? `Chapter ${order}`,
    content: legacyContentToStored(raw.content),
    order,
    kind,
    chapterNumber,
    comments: comments.length > 0 ? comments : (existing?.comments ?? []),
    notes: existing?.notes ?? [],
    tabData: tabDataFromLegacyChapter(raw.tabData, existing?.tabData),
    metadata: {
      ...baseMeta,
      customFields,
    },
    variations: mapLegacyVariations(raw.variations, existing?.variations),
    createdAt:
      typeof raw.createdAt === "string" && raw.createdAt.trim()
        ? raw.createdAt
        : (existing?.createdAt ?? new Date().toISOString()),
    updatedAt:
      typeof raw.updatedAt === "string" && raw.updatedAt.trim()
        ? raw.updatedAt
        : (existing?.updatedAt ?? new Date().toISOString()),
    rev: typeof raw.rev === "number" && Number.isFinite(raw.rev) ? raw.rev : existing?.rev,
    baseRev:
      typeof raw.baseRev === "number" && Number.isFinite(raw.baseRev)
        ? raw.baseRev
        : typeof raw.rev === "number" && Number.isFinite(raw.rev)
          ? raw.rev
          : existing?.baseRev ?? existing?.rev,
  };
}

/** Converts a legacy Prisma/API book payload into the in-memory desktop-v2 `Book` shape. */
export function legacyBookToDesktopV2(payload: unknown, localSeed?: Book): Book {
  const raw = payload && typeof payload === "object" ? (payload as Record<string, unknown>) : {};
  const base = localSeed ?? createDefaultBook();
  const localById = new Map(base.chapters.map((c) => [c.id, c]));

  const chaptersIn = Array.isArray(raw.chapters) ? raw.chapters : [];
  const chapters = chaptersIn
    .filter((c): c is Record<string, unknown> => typeof c === "object" && c !== null)
    .map((c, i) => {
      const id = typeof c.id === "string" ? c.id : "";
      return legacyChapterToDesktop(c, id ? localById.get(id) : undefined, i + 1);
    });

  const meta = raw.metadata && typeof raw.metadata === "object" ? (raw.metadata as Record<string, unknown>) : {};
  const settings =
    raw.settings && typeof raw.settings === "object" ? (raw.settings as Record<string, unknown>) : {};
  const bookId =
    typeof raw.id === "string" && raw.id.trim()
      ? String(raw.id).trim()
      : typeof base.id === "string" && base.id.trim()
        ? base.id.trim()
        : base.id;

  // Prefer current-version chapters when the payload includes version markers
  let chaptersFiltered = chapters;
  const versionsIn = Array.isArray(raw.versions) ? raw.versions : [];
  const versionSummaries = versionsIn
    .filter((v): v is Record<string, unknown> => typeof v === "object" && v !== null)
    .map((v) => ({
      id: typeof v.id === "string" ? v.id : "",
      name: typeof v.name === "string" ? v.name : "",
      status: typeof v.status === "string" ? v.status : "",
      frozenAt: typeof v.frozenAt === "string" ? v.frozenAt : null,
    }))
    .filter((v) => v.id);
  const currentVersion = versionSummaries.find((v) => v.status === "current");
  if (currentVersion && chaptersIn.some((c) => typeof (c as Record<string, unknown>).versionId === "string")) {
    chaptersFiltered = chapters.filter((_, i) => {
      const rawCh = chaptersIn[i] as Record<string, unknown>;
      const vid = typeof rawCh.versionId === "string" ? rawCh.versionId : null;
      return !vid || vid === currentVersion.id;
    });
  }

  const scoreBlend = normalizeScoreBlend(meta.scoreBlend ?? settings.scoreBlend ?? raw.scoreBlend);
  const publishing =
    (meta.publishing && typeof meta.publishing === "object" ? meta.publishing : null) ||
    (raw.publishingMetadata && typeof raw.publishingMetadata === "object" ? raw.publishingMetadata : null);

  // Prefer structured themes already in metadata; else hydrate from legacy extracted.themesAndMotifs.
  const existingThemes = Array.isArray(meta.themes) ? meta.themes : [];
  const existingMotifs = Array.isArray(meta.motifs) ? meta.motifs : [];
  const existingSymbols = Array.isArray(meta.symbols) ? meta.symbols : [];
  const extracted =
    raw.extracted && typeof raw.extracted === "object"
      ? (raw.extracted as Record<string, unknown>)
      : {};
  const themesAndMotifs =
    extracted.themesAndMotifs && typeof extracted.themesAndMotifs === "object"
      ? (extracted.themesAndMotifs as Record<string, unknown>)
      : null;
  const hydratedThemes =
    existingThemes.length > 0
      ? existingThemes
      : Array.isArray(themesAndMotifs?.themes)
        ? themesAndMotifs.themes
        : [];
  const hydratedMotifs =
    existingMotifs.length > 0
      ? existingMotifs
      : Array.isArray(themesAndMotifs?.motifs)
        ? themesAndMotifs.motifs
        : [];
  const hydratedSymbols =
    existingSymbols.length > 0
      ? existingSymbols
      : Array.isArray(themesAndMotifs?.symbols)
        ? themesAndMotifs.symbols
        : [];

  return {
    ...base,
    id: bookId,
    title: typeof raw.title === "string" ? raw.title : base.title,
    author: typeof raw.author === "string" ? raw.author : base.author,
    description: typeof raw.description === "string" ? raw.description : base.description,
    createdAt:
      typeof raw.createdAt === "string" && raw.createdAt.trim() ? raw.createdAt : base.createdAt,
    updatedAt:
      typeof raw.updatedAt === "string" && raw.updatedAt.trim() ? raw.updatedAt : base.updatedAt,
    metadata: {
      ...base.metadata,
      ...meta,
      language: typeof meta.language === "string" ? meta.language : base.metadata.language ?? "en",
      keywords: Array.isArray(meta.keywords) ? (meta.keywords as string[]) : base.metadata.keywords ?? [],
      scoreBlend,
      ...(publishing ? { publishing } : {}),
      ...(versionSummaries.length ? { cloudVersions: versionSummaries } : {}),
      ...(currentVersion ? { currentVersionId: currentVersion.id } : {}),
      ...(hydratedThemes.length || hydratedMotifs.length || hydratedSymbols.length
        ? {
            themes: hydratedThemes,
            motifs: hydratedMotifs,
            symbols: hydratedSymbols,
          }
        : {}),
    },
    chapters: withSpineOrderAndNumbers(chaptersFiltered.length > 0 ? chaptersFiltered : base.chapters),
  };
}
